import { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, TextField, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlert } from "contexts/alert";
import { Modal } from "v2/components/Modal";
import {
  CAMPAIGN_TYPE_LABEL,
  IConclusionCheck,
  LifecycleState,
  concludeImplantation,
  fetchConclusionCheck,
  fetchContractLifecycle,
  reopenImplantation,
} from "v2/services/contractLifecycle.service";

/** Spec 29/09 §3.2: âmbar (warning) para Em implantação, verde (success) para Em operação; cores vêm do tema. */
const CHIP: Record<LifecycleState, { label: string; color: "warning" | "success" | "default" }> = {
  EM_IMPLANTACAO: { label: "Em implantação", color: "warning" },
  EM_OPERACAO: { label: "Em operação", color: "success" },
  SEM_REGISTRO: { label: "Estado não registrado", color: "default" },
};

/** Soma das pessoas que ficam Em espera nesta noite em todos os tipos armados; desconhecido (null) não soma. */
export function totalHoldTonight(check: IConclusionCheck | undefined): number {
  if (!check) return 0;
  return check.armedCampaigns.reduce((sum, c) => sum + (c.wouldHoldTonight ?? 0), 0);
}

export function ConclusionCheckList({ check }: { check: IConclusionCheck }) {
  const holders = check.holdersWithoutDecision ?? null;
  const allDecided = holders?.ran === true && holders.count === 0;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, my: 2 }}>
      {check.readError && <Alert severity="info">{check.readError}</Alert>}
      {check.initialLoad && (
        <Typography>
          {check.initialLoad.rows > 0
            ? `Carga inicial registrada: ${check.initialLoad.rows} linhas`
            : "Nenhuma carga inicial registrada pela atividade Carga operadora"}
        </Typography>
      )}
      {check.pendingMovements !== null && <Typography>{`Movimentações pendentes: ${check.pendingMovements}`}</Typography>}
      {check.armedCampaigns.map((c) => (
        <Box key={c.type}>
          <Typography>
            {c.wouldHoldTonight === null
              ? `${CAMPAIGN_TYPE_LABEL[c.type]}: não foi possível calcular quantas pessoas ficam Em espera`
              : `${CAMPAIGN_TYPE_LABEL[c.type]}: ${c.wouldHoldTonight} pessoas ficam Em espera pela campanha nesta noite`}
          </Typography>
          {c.wouldHoldTonight !== null && c.wouldHoldTonight > 0 && (
            <Alert severity="warning">
              O esperado é zero: quem veio na carga já deveria ter o sim automático. Diferente de zero indica que o regime
              antigo não correu para essas pessoas; confira antes de concluir.
            </Alert>
          )}
        </Box>
      ))}
      {/* why (revisão das PRs, F5): com a contagem de titulares sem decisão, a linha do contributário substitui este aviso. */}
      {(check.eligibleOffTypes ?? []).filter(() => !holders).map((type) => (
        <Alert key={`off-${type}`} severity="info">
          {`${CAMPAIGN_TYPE_LABEL[type]}: a campanha está desligada. Se for ligada depois da conclusão, começa na mesma noite, sem período Armado.`}
        </Alert>
      ))}
      {holders && !holders.ran && !check.readError && (
        <Alert severity="info">Contributário: a rotina de optantes ainda não registrou rodada neste contrato.</Alert>
      )}
      {holders?.ran && holders.count !== null && holders.count > 0 && (
        <Alert severity="warning">{`Contributário: ${holders.count} titular(es) ainda sem decisão de optante.`}</Alert>
      )}
      {allDecided && (
        <Alert severity="info">
          Contributário: nenhum titular de hoje está sem decisão. A campanha pode ser ligada depois; quem está hoje fica decidido.
        </Alert>
      )}
    </Box>
  );
}

export function ContractLifecycleHeader({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const { openAlert } = useAlert();
  const [modal, setModal] = useState<"CONCLUDE" | "REOPEN" | null>(null);
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);

  const lifecycle = useQuery({
    queryKey: ["contract-lifecycle", workspaceId],
    queryFn: () => fetchContractLifecycle(workspaceId),
  });
  const checkKey = ["contract-lifecycle-check", workspaceId];
  const check = useQuery({
    queryKey: checkKey,
    queryFn: () => fetchConclusionCheck(workspaceId),
    enabled: modal === "CONCLUDE",
    // A contagem é do momento em que o modal abre: nunca reaproveitar uma checagem antiga.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const holdTonight = totalHoldTonight(check.data);
  // Se a contagem mudar sob o modal aberto, o aceite dado antes não vale para o número novo.
  useEffect(() => {
    setAcknowledged(false);
  }, [holdTonight]);

  if (!lifecycle.data) return null;
  const data = lifecycle.data;
  const chip = CHIP[data.state];
  const needsAcknowledgement = modal === "CONCLUDE" && holdTonight > 0;

  function close() {
    // Sem isto, reabrir o modal mostra a contagem antiga (isLoading falso durante o refetch) e
    // habilita Confirmar sobre ela, contornando o aceite. Removida, reabrir é um primeiro carregamento.
    queryClient.removeQueries({ queryKey: checkKey });
    setModal(null);
    setReason("");
    setAcknowledged(false);
  }

  async function confirm() {
    setSaving(true);
    try {
      if (modal === "CONCLUDE") await concludeImplantation(workspaceId, reason);
      if (modal === "REOPEN") await reopenImplantation(workspaceId, reason);
      await queryClient.invalidateQueries({ queryKey: ["contract-lifecycle", workspaceId] });
      openAlert(modal === "CONCLUDE" ? "Implantação concluída." : "Implantação reaberta.", "success");
      close();
    } catch (error) {
      openAlert("Não foi possível registrar a mudança de estado do contrato.", "error", error as any);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
      <Chip label={chip.label} color={chip.color} sx={{ fontWeight: 600 }} />
      {data.state !== "EM_OPERACAO" && data.permissions.canConclude && (
        <Button variant="contained" color="warning" onClick={() => setModal("CONCLUDE")}>
          Concluir implantação
        </Button>
      )}
      {data.state === "EM_OPERACAO" && data.permissions.canReopen && (
        <Button variant="text" onClick={() => setModal("REOPEN")}>
          Reabrir implantação
        </Button>
      )}

      <Modal open={modal === "CONCLUDE"} size="md">
        <Typography variant="h6">Concluir implantação</Typography>
        <Typography variant="body2">
          A conclusão é o marco do contrato: as campanhas que aguardam a implantação passam a valer a partir de agora. A
          checagem abaixo é informativa; quando há pessoas que ficarão Em espera, pede um aceite antes de concluir.
        </Typography>
        {check.isFetching && <CircularProgress size={24} />}
        {check.data && <ConclusionCheckList check={check.data} />}
        {check.isError && (
          <Alert severity="info" sx={{ my: 2 }}>
            Não foi possível calcular a checagem agora; a conclusão continua possível.
          </Alert>
        )}
        {needsAcknowledgement && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            <Alert severity="error">{`Atenção: ${holdTonight} pessoas ficarão Em espera pela campanha a partir desta noite.`}</Alert>
            <FormControlLabel
              control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
              label={`Entendo que ${holdTonight} pessoas ficarão Em espera pela campanha a partir desta noite`}
            />
          </Box>
        )}
        <TextField label="Motivo (opcional)" fullWidth value={reason} onChange={(e) => setReason(e.target.value)} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button onClick={close} disabled={saving}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={confirm} disabled={saving || check.isLoading || (needsAcknowledgement && !acknowledged)}>
            Confirmar conclusão
          </Button>
        </Box>
      </Modal>

      <Modal open={modal === "REOPEN"} size="md">
        <Typography variant="h6">Reabrir implantação</Typography>
        <Typography variant="body2">
          Reabrir não mexe nas campanhas já iniciadas: o corte continua o primeiro marco e ninguém é liberado. Um tipo
          ligado durante a reabertura fica Armado até a próxima conclusão.
        </Typography>
        <TextField label="Motivo (obrigatório)" fullWidth value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button onClick={close} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={confirm} disabled={saving || reason.trim() === ""}>
            Confirmar reabertura
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}
